import { toInputDate } from "@/utils/dateUtils";
import React, { useState, useEffect, useCallback } from "react";
import { Upload } from "lucide-react";
import { FileUploadTrigger } from "@/components/native/FileUploadTrigger";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels, commonButtonLabels, commonMiscLabels } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useInvoice } from "@/hooks/useInvoice";
import { useListing } from "@/hooks/useListing";
import { ListingService } from "@/services/listing/listingService";
import { ListingOverview } from "@/types/listing";
import { Invoice } from "@/types/payment";
import { toast } from "sonner";

import {
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  SUPPORTED_FILE_TYPES,
} from "@/config/constants";
import { ApiError } from "@/types/carAuctionClient";

function getApiErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as ApiError).message;
    if (typeof message === "string") return message;
  }
  if (error instanceof Error) return error.message;
  return "";
}

interface InvoiceUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicleCardId?: string; // If provided, skip listing selection
  listingId?: string; // If provided, use this listing directly
  regNumber?: string; // If provided, fetch listing by reg number
  suggestedAmount?: number; // Suggested amount (sale price + buyer fee)
  onSuccess?: (invoice: Invoice) => void;
  onSuccessWithMeta?: (
    invoice: Invoice,
    meta: { amount: number; dueDate: string; suggestedAmount?: number },
  ) => void;
}

interface FormData {
  file: File | null;
  id: string;
  listing: string;
  listingObj: ListingOverview | null;
  amount: string;
  dueDate: string;
}

export const InvoiceUploadDialog: React.FC<InvoiceUploadDialogProps> = ({
  open,
  onOpenChange,
  vehicleCardId,
  listingId,
  regNumber,
  suggestedAmount,
  onSuccess,
  onSuccessWithMeta,
}) => {
  const { language } = useLanguage();
  const iu = (adminLabels(language).economy?.invoiceUpload ?? {}) as Record<
    string,
    string
  >;
  const commonButtons = commonButtonLabels(language);
  const commonMisc = commonMiscLabels(language);
  const { createInvoiceAsync, isCreatingInvoice } = useInvoice();
  const { getListingsWithFilters } = useListing();

  // Fetch listing by regNumber only when regNumber prop is provided
  const listingByRegNumberQuery = getListingsWithFilters(
    { regNumber: regNumber! },
    { enabled: !!regNumber },
  );
  const listingByRegNumber = regNumber
    ? listingByRegNumberQuery.data?.listings?.[0]
    : undefined;
  const isLoadingListing = listingByRegNumberQuery.isLoading;

  const [form, setForm] = useState<FormData>({
    file: null,
    id: "",
    listing: "",
    listingObj: null,
    amount: "",
    dueDate: "",
  });

  // Prefill amount with suggestion when available
  useEffect(() => {
    if (!open) return;
    if (suggestedAmount == null) return;
    if (
      Number.isFinite(Number(form.amount)) &&
      String(form.amount).trim() !== ""
    )
      return;
    setForm((prev) => ({
      ...prev,
      amount: String(Math.round(Number(suggestedAmount))),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, suggestedAmount]);

  const [touched, setTouched] = useState({
    id: false,
    listing: false,
    amount: false,
    dueDate: false,
  });

  const [fileError, setFileError] = useState("");
  const [formError, setFormError] = useState("");
  const [listingQuery, setListingQuery] = useState("");
  const [listingDropdown, setListingDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState<ListingOverview[]>([]);
  const [isSearchingListings, setIsSearchingListings] = useState(false);
  const [totalSearchResults, setTotalSearchResults] = useState(0);
  const [currentSearchPage, setCurrentSearchPage] = useState(1);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setForm({
        file: null,
        id: "",
        listing: "",
        listingObj: null,
        amount: "",
        dueDate: "",
      });
      setTouched({
        id: false,
        listing: false,
        amount: false,
        dueDate: false,
      });
      setFileError("");
      setFormError("");
      setListingQuery("");
      setListingDropdown(false);
      setSearchResults([]);
      setTotalSearchResults(0);
      setCurrentSearchPage(1);
    }
  }, [open]);

  // Load initial COMPLETED listings when input is focused (no search)
  const handleLoadInitialListings = useCallback(async () => {
    setIsSearchingListings(true);
    try {
      const { ListingManagementService } =
        await import("@/services/listing/listingManagementService");
      const results = await ListingManagementService.getAllListings({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        status: "completed" as any, // Backend expects 'COMPLETED' but frontend type is lowercase
        page: 1,
        limit: 5,
      });

      // Backend returns 'data' not 'listings'
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const listings = results.listings || (results as any).data || [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const totalCount = results.totalCount || (results as any).count || 0;

      setSearchResults(listings);
      setTotalSearchResults(totalCount);
      setCurrentSearchPage(1);
      setListingDropdown(true);
    } catch (error) {
      setFormError(iu.errorLoadingListings ?? "Error loading listings");
    } finally {
      setIsSearchingListings(false);
    }
  }, [iu.errorLoadingListings]);

  // Search listings with search term
  const handleSearchListings = useCallback(
    async (page: number = 1, limit: number = 5, append: boolean = false) => {
      if (!listingQuery.trim()) {
        // If no search term, load initial listings
        await handleLoadInitialListings();
        return;
      }

      setIsSearchingListings(true);
      try {
        // Use admin listings endpoint for admin users
        // TODO: go via hook instead of directly to service!
        const { ListingManagementService } =
          await import("@/services/listing/listingManagementService");
        const results = await ListingManagementService.getAllListings({
          search: listingQuery,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          status: "completed" as any, // Backend expects 'COMPLETED' but frontend type is lowercase
          page,
          limit,
        });

        // Backend returns 'data' not 'listings'
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const listings = results.listings || (results as any).data || [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const totalCount = results.totalCount || (results as any).count || 0;

        if (append) {
          // Append new results for "Visa mer..."
          setSearchResults((prev) => [...prev, ...listings]);
        } else {
          // Replace results (new search)
          setSearchResults(listings);
        }

        setTotalSearchResults(totalCount);
        setCurrentSearchPage(page);
        setListingDropdown(true);
      } catch (error) {
        setFormError(iu.errorSearchingListings ?? "Error searching listings");
      } finally {
        setIsSearchingListings(false);
      }
    },
    [listingQuery, handleLoadInitialListings, iu.errorSearchingListings],
  );

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target;

    if (name === "file" && files && files[0]) {
      const file = files[0];
      setFileError("");

      // Validate file type
      const isValidType = SUPPORTED_FILE_TYPES.some((type) => {
        if (type === "image/jpeg" || type === "image/png") {
          return file.type.startsWith("image/");
        }
        return file.type === type;
      });

      if (!isValidType) {
        setFileError(
          (iu.onlyAllowed ?? "Only {types} allowed").replace(
            "{types}",
            SUPPORTED_FILE_TYPES.join(", "),
          ),
        );
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFileError(
          (iu.fileTooLarge ?? "File too large").replace(
            "{mb}",
            String(MAX_FILE_SIZE_MB),
          ),
        );
        return;
      }

      setForm((prev) => ({ ...prev, file }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    // Clear form error when user starts typing
    if (formError) {
      setFormError("");
    }
  };

  const handleLoadMore = () => {
    const nextPage = currentSearchPage + 1;
    handleSearchListings(nextPage, 5, true); // Load next 5 results
  };

  const isFormValid =
    form.file &&
    form.id.trim() &&
    (listingId ||
      (regNumber && listingByRegNumber?.id) ||
      (form.listing.trim() && form.listingObj)) &&
    form.amount.trim() &&
    form.dueDate.trim() &&
    !fileError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (!isFormValid) {
      setFormError(iu.pleaseFillAll ?? "Please fill in all required fields");
      return;
    }

    try {
      const finalListingId =
        listingId ||
        (regNumber ? listingByRegNumber?.id : undefined) ||
        form.listingObj?.id;

      if (!finalListingId) {
        setFormError(iu.noListingSelected ?? "No listing selected");
        return;
      }

      // Check if invoice already exists and ask for confirmation
      let createdInvoice: Invoice | null = null;
      try {
        // Try to create without replacing first to check if it exists
        createdInvoice = await createInvoiceAsync({
          request: {
            listingId: finalListingId,
            invoiceNumber: form.id,
            invoiceFile: form.file!,
            amount: parseFloat(form.amount),
            dueDate: form.dueDate,
          },
          replaceExisting: false,
        });

        // Success toast
        toast.success(iu.invoiceCreated ?? "Invoice created", {
          description: iu.invoiceCreatedDesc ??
            "The invoice has been created successfully.",
        });
      } catch (error: unknown) {
        if (getApiErrorMessage(error).includes("already exists")) {
          const confirmReplace = window.confirm(
            iu.replaceConfirm ??
              "An invoice already exists for this listing. Replace it?",
          );
          if (!confirmReplace) {
            return;
          }

          // Try again with replaceExisting = true
          createdInvoice = await createInvoiceAsync({
            request: {
              listingId: finalListingId,
              invoiceNumber: form.id,
              invoiceFile: form.file!,
              amount: parseFloat(form.amount),
              dueDate: form.dueDate,
            },
            replaceExisting: true,
          });

          // Success toast for replacement
          toast.success(iu.invoiceUpdated ?? "Invoice updated", {
            description: iu.invoiceUpdatedDesc ??
              "The invoice has been updated successfully.",
          });
        } else {
          // For other errors, throw to be caught by outer catch
          throw error;
        }
      }

      // Only call onSuccess and close dialog if invoice was successfully created
      if (createdInvoice) {
        onSuccess?.(createdInvoice);
        onSuccessWithMeta?.(createdInvoice, {
          amount: parseFloat(form.amount),
          dueDate: form.dueDate,
          suggestedAmount,
        });
        onOpenChange(false);
      }
    } catch (error) {
      const errorMessage =
        getApiErrorMessage(error) ||
        (commonMisc?.unknownError ?? "Unknown error");
      setFormError(
        `${iu.createError ?? "Error creating invoice"}: ${errorMessage}`,
      );
      toast.error(commonMisc?.errorTitle ?? "Error", { description: `${iu.createError ?? "Error creating invoice"}: ${errorMessage}` });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md px-4">
        <DialogHeader>
          <DialogTitle>{iu.title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <p className={`block text-sm font-medium mb-1`}>
              {iu.invoiceFile}
            </p>
            <FileUploadTrigger
              accept="application/pdf,image/*"
              onFilesSelected={(files) => {
                if (!files[0]) return;
                const dt = new DataTransfer();
                dt.items.add(files[0]);
                handleFormChange({
                  target: { name: "file", files: dt.files },
                } as React.ChangeEvent<HTMLInputElement>);
              }}
            >
              {(open) => (
                <button
                  type="button"
                  className={`flex items-center gap-2 px-4 py-2 rounded border w-full justify-center font-medium transition-colors ${
                    fileError
                      ? "border-red-500 text-red-600"
                      : "border-primary text-primary hover:bg-primary/10"
                  } cursor-pointer`}
                  onClick={open}
                >
                  <Upload className="h-5 w-5" />
                  {form.file ? iu.changeFile : iu.selectFile}
                </button>
              )}
            </FileUploadTrigger>
            {form.file && (
              <span className="text-xs mt-1 block">{form.file.name}</span>
            )}
            {fileError && (
              <div className="text-red-500 text-xs mt-1">{fileError}</div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {iu.invoiceNumber}
            </label>
            <Input
              name="id"
              value={form.id}
              onChange={handleFormChange}
              required
              className={touched.id && !form.id.trim() ? "border-red-500" : ""}
              onBlur={() => setTouched((t) => ({ ...t, id: true }))}
            />
          </div>

          {/* Only show listing selection if no listingId or regNumber provided */}
          {!listingId && !regNumber && (
            <div className="relative">
              <label className="block text-sm font-medium mb-1">
                {iu.listing}
              </label>
              <div className="flex gap-2">
                <Input
                  name="listing"
                  autoComplete="off"
                  value={listingQuery}
                  onChange={(e) => {
                    setListingQuery(e.target.value);
                    setForm((f) => ({
                      ...f,
                      listing: e.target.value,
                      listingObj: null,
                    }));
                  }}
                  placeholder={iu.listingSearchPlaceholder}
                  required
                  className={
                    touched.listing && !form.listing.trim()
                      ? "border-red-500"
                      : ""
                  }
                  onFocus={async () => {
                    if (searchResults.length === 0) {
                      // Load initial COMPLETED listings when input is focused
                      await handleLoadInitialListings();
                    } else {
                      setListingDropdown(true);
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleSearchListings(1, 5, false)}
                  disabled={isSearchingListings}
                  className="px-3"
                >
                  {isSearchingListings ? iu.searching : iu.search}
                </Button>
              </div>
              {(() => {
                return null;
              })()}
              {listingDropdown && searchResults.length > 0 && (
                <div className="absolute bg-card z-10 border rounded w-full mt-1 shadow max-h-60 overflow-y-auto">
                  <ul>
                    {searchResults.map((listing, index) => (
                      <li
                        key={listing.id || index}
                        className="px-3 py-2 hover:bg-accent/10 cursor-pointer border-b last:border-b-0"
                        onMouseDown={() => {
                          const label = `${
                            listing.vehicleDetails?.regNumber || ""
                          } - ${listing.title}`;
                          setForm((f) => ({
                            ...f,
                            listing: label,
                            listingObj: listing,
                          }));
                          setListingQuery(label);
                          setListingDropdown(false);
                        }}
                      >
                        {listing.vehicleDetails?.regNumber || ""} -{" "}
                        {listing.title}
                      </li>
                    ))}
                  </ul>
                  {searchResults.length < totalSearchResults && (
                    <button
                      type="button"
                      onClick={handleLoadMore}
                      disabled={isSearchingListings}
                      className="w-full px-3 py-2 text-sm text-primary hover:bg-accent/10 border-t"
                    >
                      {isSearchingListings
                        ? iu.loadingMore
                        : (
                            iu.loadMoreRemaining ??
                            "Show more... ({count} remaining)"
                          ).replace(
                            "{count}",
                            String(totalSearchResults - searchResults.length),
                          )}
                    </button>
                  )}
                </div>
              )}
              {listingDropdown &&
                searchResults.length === 0 &&
                !isSearchingListings &&
                listingQuery.trim() && (
                  <div className="absolute bg-card z-10 border rounded w-full mt-1 shadow px-3 py-2 text-sm text-muted-foreground">
                    {iu.noResults}
                  </div>
                )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">
              {iu.amount}
            </label>
            <Input
              name="amount"
              type="number"
              value={form.amount}
              onChange={handleFormChange}
              required
              min={1}
              className={
                touched.amount && !form.amount.trim() ? "border-red-500" : ""
              }
              onBlur={() => setTouched((t) => ({ ...t, amount: true }))}
            />
            {typeof suggestedAmount === "number" &&
              Number.isFinite(suggestedAmount) &&
              suggestedAmount > 0 && (
                <div className="mt-1 space-y-1">
                  <p className="text-xs text-muted-foreground">
                    {(iu.suggestedAmountLabel ?? "Prisförslag") +
                      `: ${Math.round(suggestedAmount).toLocaleString("sv-SE")} kr`}
                  </p>
                  {form.amount.trim() !== "" &&
                    Math.round(Number(form.amount || 0)) !==
                      Math.round(suggestedAmount) && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                        {iu.amountDiffersFromSuggestion ??
                          "Beloppet skiljer sig från prisförslaget. Detta sparas som det slutgiltiga beloppet som köparen ser."}
                      </p>
                    )}
                </div>
              )}
          </div>
          <div className="overflow-x-hidden px-2">
            <label className="block text-sm font-medium mb-1">
              {iu.dueDate}
            </label>
            <Input
              name="dueDate"
              type="date"
              value={form.dueDate || ""}
              onChange={handleFormChange}
              required
              min={toInputDate(new Date())}
              className={
                touched.dueDate && !form.dueDate?.trim() ? "border-red-500" : ""
              }
              onBlur={() => setTouched((t) => ({ ...t, dueDate: true }))}
            />
          </div>
          {formError && <div className="text-red-500 text-sm">{formError}</div>}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              type="button"
              onClick={() => onOpenChange(false)}
            >
              {commonButtons?.cancel ?? "Cancel"}
            </Button>
            <Button type="submit" disabled={!isFormValid || isCreatingInvoice}>
              {isCreatingInvoice ? iu.creating : iu.submit}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
